import { getBioRepGroupKey } from '@resistapp/common/environments';
import { FullSample, XAxisMode } from '@resistapp/common/types';
import { isValid } from 'date-fns';
import { chain, countBy, values } from 'lodash';
import { ensureDate } from './friendly';

/**
 * Determine which XAxisMode values are applicable for a given set of samples.
 *
 * 1. ORDINAL_GROUPED is always available.
 * 2. TIME is available when *every* sample has a `time` value and there are
 *    at least two distinct time points across the data set.
 * 3. ORDINAL_INDIVIDUAL is available when any bio-replicate group (computed
 *    with `getBioRepGroupKey`) contains more than one sample.
 */
export function determineAvailableXAxisModes(samples: FullSample[]): XAxisMode[] {
  const modesAcc: XAxisMode[] = [XAxisMode.ORDINAL_GROUPED];
  const countsByBioKey = countBy(samples, s => getBioRepGroupKey(s));
  const hasMultiSampleBioGroup = values(countsByBioKey).some(count => count > 1);
  if (hasMultiSampleBioGroup) {
    modesAcc.push(XAxisMode.ORDINAL_INDIVIDUAL);
  }
  if (isTimeModeAvailable(samples)) {
    modesAcc.push(XAxisMode.TIME);
  }
  return modesAcc;
}

/**
 * Checks if the XAxisMode.TIME is available for a given set of samples.
 * TIME mode is available if:
 * 1. Every sample in the input array has a valid `time` property.
 * 2. At least one environment contains more than one distinct collection time.
 */
export function isTimeModeAvailable(
  samples: ReadonlyArray<{
    time?: Date | string | null | undefined;
    environmentId?: number | null;
    environment?: { id?: number | null } | Record<string, unknown>;
  }>,
): boolean {
  if (!samples.length) {
    return false;
  }

  // 1. All samples must have a valid time.
  const allSamplesHaveValidTime = samples.every(sample => sample.time && isValid(new Date(sample.time)));
  if (!allSamplesHaveValidTime) {
    return false;
  }

  // 2. Build distinct time sets per environment and ensure one env has >1 dates.
  const timesByEnv: Partial<Record<string, Set<number>>> = {};
  samples.forEach(sample => {
    const sampleTime = ensureDate(sample.time).getTime();

    const envKeyRaw = sample.environmentId ?? (sample.environment && (sample.environment as { id?: number | null }).id);
    const envKey = envKeyRaw !== null && envKeyRaw !== undefined ? String(envKeyRaw) : 'unknown';
    if (!timesByEnv[envKey]) {
      timesByEnv[envKey] = new Set<number>();
    }
    timesByEnv[envKey].add(sampleTime);
  });

  return chain(timesByEnv)
    .values()
    .compact()
    .some(set => set.size > 1)
    .value();
}
